import { useQuery } from "@tanstack/react-query";

import { RewardI } from "@/api/routes/incentives";
import { useApiClient } from "@/context/api-client";
import { isManageSwitchedOff } from "@/lib/manage/errors";

import { RewardsQueryKeys } from "./keys";

/**
 * The caller's Rewards (C-16 S1). No retries: a 404 + 1035 means Rewards is
 * switched off (`rewards_enabled`), which the page and the nav read as "off".
 */
export const useFetchRewards = (enabled = true) => {
  const api = useApiClient();

  return useQuery({
    queryKey: RewardsQueryKeys.list(),
    queryFn: async () => {
      const { data } = await api.rewards.getRewards();

      return {
        active: data?.active ?? [],
        upcoming: data?.upcoming ?? [],
        past: data?.past ?? [],
      };
    },
    enabled,
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
};

/** One reward's standings for a period (default: the running one). */
export const useFetchReward = (
  id: string | null,
  periodStart: string | null
) => {
  const api = useApiClient();

  return useQuery({
    queryKey: RewardsQueryKeys.detail(id ?? "", periodStart),
    queryFn: async () => {
      const { data } = await api.rewards.getReward(id ?? "", periodStart);

      return data;
    },
    enabled: !!id,
    retry: false,
    staleTime: 60_000,
    // Keep the last period's standings while another period loads, never
    // another contest's.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === (id ?? "") ? previous : undefined,
    refetchOnWindowFocus: false,
  });
};

export interface RewardAnnouncementsStateI {
  isOn: boolean;
  rewards: Array<RewardI>;
}

const useRewardAnnouncementsQuery = <T>(
  select: (state: RewardAnnouncementsStateI) => T,
  enabled = true
) => {
  const api = useApiClient();

  return useQuery({
    queryKey: RewardsQueryKeys.announcements(),
    queryFn: async (): Promise<RewardAnnouncementsStateI> => {
      try {
        const { data } = await api.rewards.getRewardAnnouncements();

        return { isOn: true, rewards: data?.rewards ?? [] };
      } catch (error) {
        if (isManageSwitchedOff(error)) {
          return { isOn: false, rewards: [] };
        }

        throw error;
      }
    },
    select,
    enabled,
    retry: false,
    // Stale after 30 minutes: the next screen that reads it rechecks the
    // switch, so a switch-on or a kill switch shows without a reload.
    staleTime: 30 * 60_000,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

/**
 * Whether Rewards is switched on, asked once per app load: the nav entry
 * shows only on `true`. Fails closed: loading, off (404 + 1035) and any error
 * all hide it. Shares its request with the What's new announcements.
 */
const selectIsOn = (state: RewardAnnouncementsStateI) => state.isOn;

export const useFetchRewardsSwitch = () =>
  useRewardAnnouncementsQuery(selectIsOn);

const selectState = (state: RewardAnnouncementsStateI) => state;

/**
 * Whether Rewards is on, and the running rewards inside their announce window
 * (C16-D330), once per load: the What's new source.
 */
export const useFetchRewardAnnouncements = (enabled = true) =>
  useRewardAnnouncementsQuery(selectState, enabled);
